import { ApexOptions } from 'apexcharts';
import { TOKENS } from '../../../components/design-system';

// Single ApexCharts theme used by every Graphic on the dashboard. Pulls
// from design tokens so the charts share the same visual language as the
// rest of the page — instead of inheriting Metronic's chartColor=danger
// /warning-active/etc. palette.

const TONE_TO_COLOR: Record<string, string> = {
  danger: TOKENS.color.severity.high.text,
  high: TOKENS.color.severity.high.text,
  'warning-active': TOKENS.color.severity.medium.text,
  warning: TOKENS.color.severity.medium.text,
  medium: TOKENS.color.severity.medium.text,
  info: TOKENS.color.action,
  primary: TOKENS.color.action,
  secondary: TOKENS.color.muted,
  low: TOKENS.color.severity.low.text,
  success: TOKENS.color.severity.low.text,
};

export function resolveChartColor(chartColor: string): string {
  return TONE_TO_COLOR[chartColor] ?? TOKENS.color.action;
}

// Returns ApexOptions defaults that produce the "premium" look:
// • no surrounding background (PageCard supplies it)
// • thin gridlines using design tokens
// • token-driven typography
// • soft x/y axis labels
export function premiumChartDefaults(): Partial<ApexOptions> {
  return {
    chart: {
      background: 'transparent',
      toolbar: { show: false },
      fontFamily: 'inherit',
    },
    grid: {
      borderColor: TOKENS.color.divider,
      strokeDashArray: 0,
      padding: { top: 0, right: 8, bottom: 0, left: 8 },
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
    dataLabels: { enabled: false },
    xaxis: {
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: {
          colors: TOKENS.color.muted,
          fontSize: '11px',
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: TOKENS.color.muted,
          fontSize: '11px',
          fontWeight: 500,
        },
      },
    },
    tooltip: {
      theme: 'light',
      style: { fontSize: '12px', fontFamily: 'inherit' },
    },
    legend: {
      labels: { colors: TOKENS.color.muted },
      fontSize: '12px',
      fontWeight: 500,
    },
  };
}

// Helper to deep-merge premium defaults into a graphic's chart options.
// ApexCharts accepts deeply-nested overrides, so a shallow Object.assign
// would lose the existing nested chart/grid/axis config.
export function withPremiumTheme(options: ApexOptions): ApexOptions {
  const d = premiumChartDefaults();
  return {
    ...options,
    chart: { ...(options.chart ?? {}), ...(d.chart ?? {}) },
    grid: { ...(options.grid ?? {}), ...(d.grid ?? {}) },
    dataLabels: { ...(options.dataLabels ?? {}), ...(d.dataLabels ?? {}) },
    xaxis: {
      ...(options.xaxis ?? {}),
      ...(d.xaxis ?? {}),
      labels: { ...(options.xaxis?.labels ?? {}), ...(d.xaxis?.labels ?? {}) },
    },
    yaxis: { ...(d.yaxis ?? {}), ...(options.yaxis ?? {}) },
    tooltip: { ...(options.tooltip ?? {}), ...(d.tooltip ?? {}) },
    legend: { ...(options.legend ?? {}), ...(d.legend ?? {}) },
  };
}
